import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { CourseOffering } from 'app/library/course-offering/models/course-offering.model';
import { ModuleType } from 'app/library/module/enums/moduleType.enum';
import { Module } from 'app/library/module/models/module.model';
import { TrainingApprovalRequest } from 'app/library/training-approval-request/models/training-approval-request.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { User } from 'app/projects/user/src/lib/models/user';
import { race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateExtendedAttribute,
    CreateExtendedAttributeFailure,
    CreateExtendedAttributeSuccess,
    DeleteExtendedAttribute,
    DeleteExtendedAttributeFailure,
    DeleteExtendedAttributeSuccess,
    FetchAllAdminExtendedAttributes,
    FetchAllAdminExtendedAttributesByModuleType,
    FetchAllAdminExtendedAttributesByModuleTypeFailure,
    FetchAllAdminExtendedAttributesByModuleTypeSuccess,
    FetchAllAdminExtendedAttributesFailure,
    FetchAllAdminExtendedAttributesSuccess,
    FetchAllExtendedAttributes,
    FetchAllExtendedAttributesFailure,
    FetchAllExtendedAttributesSuccess,
    UpdateExtendedAttribute,
    UpdateExtendedAttributeFailure,
    UpdateExtendedAttributeSuccess,
    UpdateExtendedAttributeTranslations,
    UpdateExtendedAttributeTranslationsFailure,
    UpdateExtendedAttributeTranslationsSuccess,
    UpdateExtendedAttributeValue,
    UpdateExtendedAttributeValueFailure,
    UpdateExtendedAttributeValueSuccess,
} from '../actions';
import { ExtendedAttributeCategoryEnum } from '../enums/extended-attribute-category.enum';
import { IEditExtendedAttributeValue } from '../interfaces/edit-extended-attribute-value.interface';
import { ExtendedAttribute } from '../models/extended-attribute';
import { ExtendedAttributeState } from '../models/extended-attribute.state';

@Injectable()
export class ExtendedAttributeService {
    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, private _action$: Actions) {}

    mapOptionsToUserAttributes(user: User, extendedAttributes: ExtendedAttribute[], filter?: 'visible' | 'hidden'): User {
        const userAttributes = user.attributes
            .map((userAttr) => {
                const matchingAttr = extendedAttributes.find((attr) => userAttr.attribute.id === attr.id);
                if (matchingAttr) {
                    userAttr.attribute.options = [...matchingAttr.options];
                }
                return userAttr;
            })
            .filter((attr) => {
                if (filter === 'visible') {
                    return attr.attribute.visible;
                } else if (filter === 'hidden') {
                    return !attr.attribute.visible;
                } else {
                    return true;
                }
            });

        return (user = Object.assign(new User(), user, {
            attributes: userAttributes,
        }));
    }

    mapOptionsToApprovalRequestAttributes(
        request: TrainingApprovalRequest,
        extendedAttributes: ExtendedAttribute[],
        hideNonMandatoryRequestAttributes: boolean = false
    ): TrainingApprovalRequest {
        const requestAttributes = request.attributes.map((requestAttr) => {
            const matchingAttr = extendedAttributes?.find((attr) => requestAttr.attribute.id === attr.id);
            if (matchingAttr) {
                requestAttr.attribute.options = [...matchingAttr.options];
            }
            return requestAttr;
        });

        return (request = Object.assign(new TrainingApprovalRequest(), request, {
            attributes: requestAttributes,
            requestAttributes: requestAttributes.filter((attr) =>
                hideNonMandatoryRequestAttributes
                    ? attr.attribute.category === ExtendedAttributeCategoryEnum.REQUEST_FIELD &&
                      (attr.value.valueAddress || attr.value.valueDate || attr.value.valueNumber || attr.value.valueText)
                    : attr.attribute.category === ExtendedAttributeCategoryEnum.REQUEST_FIELD
            ),
            approvalAttributes: requestAttributes.filter((attr) => attr.attribute.category === ExtendedAttributeCategoryEnum.APPROVAL_FIELD),
        }));
    }

    mapOptionsToCourseAttributes(offering: CourseOffering, extendedAttributes: ExtendedAttribute[], filter?: 'visible' | 'hidden'): CourseOffering {
        const courseAttributes = offering.attributes
            .map((courseAttr) => {
                const matchingAttr = extendedAttributes.find((attr) => courseAttr.attribute.id === attr.id);

                if (matchingAttr) {
                    courseAttr.attribute.options = [...matchingAttr.options];
                }
                return courseAttr;
            })
            .filter((attr) => {
                if (filter === 'visible') {
                    return attr.attribute.visible;
                } else if (filter === 'hidden') {
                    return !attr.attribute.visible;
                } else {
                    return true;
                }
            });

        return (offering = Object.assign(new CourseOffering(), offering, {
            attributes: courseAttributes,
        }));
    }

    mapOptionsToModuleAttributes(module: Module, extendedAttributes: ExtendedAttribute[], filter?: 'visible' | 'hidden'): Module {
        const moduleAttributes = module.attributes
            .map((moduleAttr) => {
                const matchingAttr = extendedAttributes.find((attr) => moduleAttr.attribute.id === attr.id);

                if (matchingAttr) {
                    moduleAttr.attribute.options = [...matchingAttr.options];
                }
                return moduleAttr;
            })
            .filter((attr) => {
                if (filter === 'visible') {
                    return attr.attribute.visible;
                } else if (filter === 'hidden') {
                    return !attr.attribute.visible;
                } else {
                    return true;
                }
            });

        return (module = Object.assign(new Module(), module, {
            attributes: moduleAttributes,
        }));
    }

    getExtendedAttributes(): ExtendedAttribute[] {
        return this._store.selectSnapshot(ExtendedAttributeState.getEntities);
    }

    fetchAdminExtendedAttributes(categories: ExtendedAttributeCategoryEnum[], enabledForFilter?: string): Promise<boolean> {
        this._store.dispatch(new FetchAllAdminExtendedAttributes({ categories, enabledForFilter }));

        return this._actionHelper.race(FetchAllAdminExtendedAttributesSuccess, FetchAllAdminExtendedAttributesFailure).toPromise();
    }

    fetchAdminExtendedAttributesByModuleType(category: ExtendedAttributeCategoryEnum, enabledForFilter?: string): Promise<ExtendedAttribute[]> {
        this._store.dispatch(new FetchAllAdminExtendedAttributesByModuleType({ category, enabledForFilter }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAllAdminExtendedAttributesByModuleTypeSuccess),
                map((action: FetchAllAdminExtendedAttributesByModuleTypeSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAllAdminExtendedAttributesByModuleTypeFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    fetchExtendedAttributes(categories: ExtendedAttributeCategoryEnum[], enabledForFilter?: ModuleType): Promise<boolean> {
        this._store.dispatch(new FetchAllExtendedAttributes({ categories, enabledForFilter }));

        return this._actionHelper.race(FetchAllExtendedAttributesSuccess, FetchAllExtendedAttributesFailure).toPromise();
    }

    async createExtendedAttribute(entity: ExtendedAttribute): Promise<boolean> {
        this._store.dispatch(new CreateExtendedAttribute(entity));

        return await this._actionHelper.race(CreateExtendedAttributeSuccess, CreateExtendedAttributeFailure).toPromise();
    }

    async updateExtendedAttribute(entity: ExtendedAttribute): Promise<boolean> {
        this._store.dispatch(new UpdateExtendedAttribute(entity));

        return await this._actionHelper.race(UpdateExtendedAttributeSuccess, UpdateExtendedAttributeFailure).toPromise();
    }

    async updateExtendedAttributeTranslations(entity: ExtendedAttribute): Promise<boolean> {
        this._store.dispatch(new UpdateExtendedAttributeTranslations(entity));

        return await this._actionHelper.race(UpdateExtendedAttributeTranslationsSuccess, UpdateExtendedAttributeTranslationsFailure).toPromise();
    }

    async deleteExtendedAttribute(id: string): Promise<boolean> {
        this._store.dispatch(new DeleteExtendedAttribute(id));

        return await this._actionHelper.race(DeleteExtendedAttributeSuccess, DeleteExtendedAttributeFailure).toPromise();
    }

    async updateExtendedAttributeValue(entity: IEditExtendedAttributeValue): Promise<boolean> {
        this._store.dispatch(new UpdateExtendedAttributeValue(entity));

        return await this._actionHelper.race(UpdateExtendedAttributeValueSuccess, UpdateExtendedAttributeValueFailure).toPromise();
    }
}
